"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.htmlToSlate = void 0;
const types_1 = require("../types");
const guards_1 = require("../types/guards");
const lodash_1 = __importDefault(require("lodash"));
const Node = {
    TEXT_NODE: 3,
    ELEMENT_NODE: 1,
};
const getDomParser = () => {
    let DOMParser;
    let JSDOM;
    if (typeof window !== 'undefined' && window.DOMParser) {
        DOMParser = window.DOMParser;
    }
    else {
        try {
            const jsdom = require('jsdom');
            JSDOM = jsdom.JSDOM;
        }
        catch (error) {
            console.error('Не удалось загрузить jsdom в Node.js окружении:', error);
        }
    }
    return { DOMParser, JSDOM };
};
const flattenInlineElement = (nodes) => {
    const res = nodes.flatMap((node) => {
        if (node.type === 'text') {
            return node.data;
        }
        if (node.type === 'inline' || node.type === 'element') {
            return flattenInlineElement(node.children);
        }
        return [];
    });
    if (res.length === 0) {
        return [
            {
                text: '',
            },
        ];
    }
    return res;
};
const flattenBlockElement = (nodes, type, marks) => {
    var _a;
    const temp = nodes.flatMap((node) => {
        if (node.type === 'element' && !(0, guards_1.isInlineElement)(node.data)) {
            return [...flattenInlineElement(node.children), { type: 'break', children: [] }];
        }
        return [node];
    });
    if (((_a = temp[temp.length - 1]) === null || _a === void 0 ? void 0 : _a.type) !== 'break') {
        temp.push({ type: 'break', children: [] });
    }
    const filteredNodes = filterDoubleBreak(temp);
    let inlines = [];
    const res = [];
    for (const node of filteredNodes) {
        if (node.type === 'break') {
            res.push(Object.assign({ type, children: inlines }, marks));
            inlines = [];
        }
        else {
            if ((0, guards_1.isTextNode)(node)) {
                inlines.push(node);
            }
            else if (node.type === 'text') {
                inlines.push(node.data);
            }
            else if (node.type === 'inline') {
                inlines.push(Object.assign(Object.assign({}, node.data), { children: flattenInlineElement(node.children) }));
            }
        }
    }
    return res;
};
const filterDoubleBreak = (nodes) => {
    const res = [];
    const isBreak = (node) => (node === null || node === void 0 ? void 0 : node.type) === 'break' || node === 'br';
    for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (isBreak(node)) {
            while (isBreak(nodes[i + 1])) {
                i++;
            }
        }
        res.push(node);
    }
    return res;
};
const flattenDefault = (nodes, root = false) => {
    const tmp = nodes.flatMap((node) => {
        if (node.type === 'element') {
            const marks = lodash_1.default.pick(node.data, [types_1.SlateElementMark.textAlign]);
            if (node.data.type === types_1.SlateElementType.elementDefault && lodash_1.default.isEmpty(marks)) {
                return [...flattenDefault(node.children), 'br'];
            }
            else if ((0, guards_1.isListElement)(node.data)) {
                const el = Object.assign(Object.assign({}, node.data), { children: flattenBlockElement(node.children, types_1.SlateElementType.listItem, marks).map((e) => (Object.assign(Object.assign({}, e), { type: types_1.SlateElementType.listItem }))) });
                return [el];
            }
            else if ((0, guards_1.isBlockElement)(node.data)) {
                return flattenBlockElement(node.children, node.data.type, marks);
            }
        }
        if (node.type === 'text') {
            return [node.data];
        }
        if (node.type === 'inline') {
            return [Object.assign(Object.assign({}, node.data), { children: flattenInlineElement(node.children) })];
        }
        if (node.type === 'break') {
            return ['br'];
        }
        return [];
    });
    if (root && tmp[tmp.length - 1] !== 'br') {
        tmp.push('br');
    }
    const filteredNodes = filterDoubleBreak(tmp);
    const res = [];
    let inlines = [];
    for (const node of filteredNodes) {
        if (node === 'br') {
            if (inlines.length > 0) {
                res.push({
                    type: types_1.SlateElementType.elementDefault,
                    children: inlines,
                });
                inlines = [];
            }
        }
        else {
            if ((0, guards_1.isTextNode)(node) || node.type === 'text' || (0, guards_1.isInlineElement)(node)) {
                inlines.push(node);
            }
            else {
                if (inlines.length > 0) {
                    res.push({
                        type: types_1.SlateElementType.elementDefault,
                        children: inlines,
                    });
                    inlines = [];
                }
                if (!node.children.length) {
                    node.children.push({ text: '' });
                }
                res.push(node);
            }
        }
    }
    if (inlines.length > 0) {
        res.push({
            type: types_1.SlateElementType.elementDefault,
            children: inlines,
        });
    }
    return res;
};
const parseElements = (node, context) => {
    return Array.from(node.childNodes).flatMap((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
            return [
                {
                    type: 'text',
                    data: Object.assign(Object.assign({}, context.textMarks), { text: child.textContent || '' }),
                    children: [],
                },
            ];
        }
        if (child.nodeType === Node.ELEMENT_NODE && !child.hasAttribute('hidden')) {
            return parseElementNode(child, context);
        }
        return [];
    });
};
const parseStyle = (node) => {
    const css = node.getAttribute('style');
    if (!css) {
        return {};
    }
    const res = {};
    const pairs = css.split(';');
    for (const pair of pairs) {
        const [key, value] = pair.split(':');
        if (key && value) {
            res[key.trim()] = value.trim();
        }
    }
    return res;
};
const getNodeTextMarks = (node) => {
    const marks = {};
    const styleAttr = parseStyle(node);
    const style = node.style;
    const tagName = node.tagName.toLowerCase();
    if (style) {
        if (['bold', '600', '700', '800'].includes(style.fontWeight)) {
            marks[types_1.SlateMark.bold] = true;
        }
        if (style.fontStyle === 'italic') {
            marks[types_1.SlateMark.italic] = true;
        }
        if (style.textDecoration === 'underline') {
            marks[types_1.SlateMark.underline] = true;
        }
        if (style.textDecoration === 'line-through') {
            marks[types_1.SlateMark.strikethrough] = true;
        }
        if (style.backgroundColor || styleAttr['background-color']) {
            marks[types_1.SlateMark.backgroundColor] = style.backgroundColor || styleAttr['background-color'];
        }
        if (style.color || styleAttr.color) {
            marks[types_1.SlateMark.color] = style.color || styleAttr.color;
        }
        if (style.textTransform) {
            marks[types_1.SlateMark.textTransform] = style.textTransform;
        }
        if (style.textTransform === 'capitalize') {
            marks[types_1.SlateMark.textTransform] = 'capitalize';
        }
        if (style.textTransform === 'lowercase') {
            marks[types_1.SlateMark.textTransform] = 'lowercase';
        }
    }
    if (tagName === 'sub') {
        marks[types_1.SlateMark.register] = 'sub';
    }
    if (tagName === 'sup') {
        marks[types_1.SlateMark.register] = 'sup';
    }
    if (tagName === 'b' || tagName === 'strong') {
        marks[types_1.SlateMark.bold] = true;
    }
    if (tagName === 'i' || tagName === 'em') {
        marks[types_1.SlateMark.italic] = true;
    }
    if (tagName === 'u') {
        marks[types_1.SlateMark.underline] = true;
    }
    if (tagName === 's' || tagName === 'del') {
        marks[types_1.SlateMark.strikethrough] = true;
    }
    return marks;
};
const getNodeElementMarks = (node) => {
    const marks = {};
    const style = node.style;
    if (style) {
        if (style.textAlign) {
            marks[types_1.SlateElementMark.textAlign] = style.textAlign;
        }
    }
    return marks;
};
const getMode = (node, context) => {
    if (context.mode === 'text') {
        return 'text';
    }
    const tag = node.tagName.toLowerCase();
    const type = mapHtmlTagToSlateType(tag);
    if (tag === 'a') {
        return 'text';
    }
    if (type) {
        return 'inline';
    }
    return 'default';
};
const TAGS_WITH_BREAK = ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'blockquote'];
const parseElementNode = (node, context) => {
    var _a;
    const tag = node.tagName.toLowerCase();
    const type = mapHtmlTagToSlateType(tag, node.getAttribute('role'));
    const mode = getMode(node, context);
    const nodeContext = {
        type: context.type,
        textMarks: Object.assign(Object.assign({}, context.textMarks), getNodeTextMarks(node)),
        elementMarks: Object.assign(Object.assign({}, context.elementMarks), getNodeElementMarks(node)),
        mode,
    };
    const children = parseElements(node, nodeContext);
    if (tag === 'br' && context.mode !== 'text') {
        return [{ type: 'break', children: [] }];
    }
    if (tag === 'meta') {
        return [];
    }
    if (tag === 'code') {
        return [
            {
                type: 'inline',
                data: {
                    type: types_1.SlateElementType.code,
                    children: [],
                },
                children,
            },
        ];
    }
    if (tag === 'a') {
        const url = node.getAttribute('href');
        if (url.startsWith('~')) {
            const [section, block] = url
                .slice(1)
                .split('/')
                .filter((a) => a);
            const value = { section };
            if (block) {
                value.block = block;
            }
            return [
                {
                    type: 'inline',
                    data: {
                        type: types_1.SlateElementType.crossLink,
                        value,
                        children: [],
                    },
                    children,
                },
            ];
        }
        if (url.startsWith('@')) {
            const employeeId = url.slice(1);
            return [
                {
                    type: 'inline',
                    data: {
                        type: types_1.SlateElementType.mention,
                        employeeId,
                        children: [],
                    },
                    children,
                },
            ];
        }
        return [
            {
                type: 'inline',
                data: {
                    type: types_1.SlateElementType.link,
                    url: node.getAttribute('href') || '',
                    children: [],
                },
                children,
            },
        ];
    }
    if (tag === 'span' && node.getAttribute('title') && ((_a = node.firstChild) === null || _a === void 0 ? void 0 : _a.hasAttribute('hidden'))) {
        const value = node.firstChild.textContent || '';
        return [
            {
                type: 'inline',
                data: {
                    type: types_1.SlateElementType.annotation,
                    value,
                    children: [],
                },
                children,
            },
        ];
    }
    if (mode === 'text') {
        return children;
    }
    if (context.mode === 'default' && type) {
        return [
            {
                type: 'element',
                data: Object.assign(Object.assign({ type, children: [] }, context.elementMarks), nodeContext.elementMarks),
                children,
            },
        ];
    }
    if (context.mode === 'inline' && (type || TAGS_WITH_BREAK.includes(tag))) {
        return [...children, { type: 'break', children: [] }];
    }
    if (!lodash_1.default.isEmpty(nodeContext.elementMarks) && TAGS_WITH_BREAK.includes(tag)) {
        return [
            {
                type: 'element',
                data: Object.assign({ type: types_1.SlateElementType.elementDefault, children: [] }, nodeContext.elementMarks),
                children,
            },
        ];
    }
    else if (TAGS_WITH_BREAK.includes(tag)) {
        children.push({ type: 'break', children: [] });
    }
    return children;
};
const mapHtmlTagToSlateType = (tag, role) => {
    switch (tag) {
        case 'blockquote':
            return types_1.SlateElementType.blockquote;
        case 'ul':
            return types_1.SlateElementType.bulletedList;
        case 'ol':
            return types_1.SlateElementType.numberedList;
        case 'li':
            return types_1.SlateElementType.listItem;
        case 'h1':
            return types_1.SlateElementType.heading1;
        case 'h2':
            return types_1.SlateElementType.heading2;
        case 'h3':
            return types_1.SlateElementType.heading3;
        case 'h4':
            return types_1.SlateElementType.heading4;
        case 'div':
            switch (role) {
                case 'caption':
                    return types_1.SlateElementType.caption;
                default:
                    return undefined;
            }
        default:
            return undefined;
    }
};
const parseHtml = (html) => {
    const { DOMParser, JSDOM } = getDomParser();
    if (DOMParser) {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(html, 'text/html');
        return parsed;
    }
    else if (JSDOM) {
        const { document } = new JSDOM(html).window;
        return document;
    }
    else {
        throw new Error('No DOMParser or JSDOM available.');
    }
};
const prepareDocument = (document) => {
    const boldElements = document.querySelectorAll('body > b');
    boldElements.forEach((boldElement) => {
        const parent = boldElement.parentNode;
        if (!parent) {
            return;
        }
        while (boldElement.firstChild) {
            parent.insertBefore(boldElement.firstChild, boldElement);
        }
        parent.removeChild(boldElement);
    });
    return document;
};
const prepareHtml = (htmlRaw) => {
    return (htmlRaw
        .replace(/>\s*\n\s*</g, '><')
        .trim());
};
const htmlToSlate = (htmlRaw) => {
    const html = prepareHtml(htmlRaw);
    const documentRaw = parseHtml(html);
    const document = prepareDocument(documentRaw);
    const parsedNodes = parseElements(document.body, {
        type: types_1.SlateElementType.elementDefault,
        textMarks: {},
        elementMarks: {},
        mode: 'default',
    });
    const res = flattenDefault(parsedNodes, true);
    if (res.length === 0) {
        return [{ type: types_1.SlateElementType.elementDefault, children: [{ text: '' }] }];
    }
    return res;
};
exports.htmlToSlate = htmlToSlate;
